import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteJson, GetJson, PostJson, PutJson } from '@awp/helpers/fetchReact';
import { FORM_MODE, Permissions, REST_API, SENSOR_PLACEMENT_CONFIG, STATE_STATUS } from '@awp/constants';
import { Card, Col, Container, Form as ReactForm, Row } from 'react-bootstrap';
import {
  FormDatePicker,
  FormInput,
  FormSelect,
  FormSpatialInput,
  FormTextArea,
} from '@awp/components/Common/AwpFormInputs';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { isIntStr, isValueEmpty } from '@awp/helpers/utilsReact';
import { format as formatDate } from 'date-fns';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import AwpSpinner from '@awp/components/Common/AwpSpinner';
import {
  selectAllWeatherStationStatuses,
  selectAllWeatherStationStatuseTypes,
} from '@stores/weatherStationStatusesSlice';
import {
  fetchSensorEquipmentPacket,
  setEditing,
  setOrignPlacement,
  setOrignSegment,
  setPlacement,
  setSaveSegmentError,
  setSaveSegmentStatus,
  setSaveStatus,
  setSegment,
  setSotId,
  setStatus,
  setWeatherStationId,
} from '@awp/stores/sensorEquipmentPacketSlice';
import PlacementList from '@awp/components/WeatherStationAdmin/Placement/PlacementList';
import { fetchCameraViews, setCameraStatus } from '@awp/stores/cameraViewsSlice';
import WeatherStationCameraView from '@components/cameras/WeatherStationCameraView';
import { validateBcLatitude, validateBcLongitude } from '@awp/helpers/validation';
import { WeatherStationService } from '@components/WeatherStationAdmin/WeatherStationService';
import isEqual from 'lodash/isEqual';

const WeatherStationEdit = ({ setMode, mode, weatherStationId, data, setData, setLoading, loading }) => {
  const dispatch = useDispatch();
  const allWeatherStationStatuses = useSelector(selectAllWeatherStationStatuses);
  const allWeatherStationTypes = useSelector(selectAllWeatherStationStatuseTypes);

  const [weatherStationData, setWeatherStationData] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCoordRequired, setIsCoordRequired] = useState(false);
  const [initialDto, setInitialDto] = useState({});

  const crudService = new WeatherStationService(mode);
  const { readOnly, title, buttonLabel, successMessage, entityName } = crudService.crud;

  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      setIsCoordRequired(true);
      const entity = {
        Number: '',
        Name: '',
        ShortName: '',
        Description: '',
        StatusTypeId: null,
        TypeId: null,
      };
      setWeatherStationData(entity);
      setInitialDto({
        ...crudService.mapInitialValues({
          entity,
          getDefaultStatusType,
        }),
      });
      return;
    }
    if (mode === FORM_MODE.VIEW) {
      dispatch(setWeatherStationId(weatherStationId));
      cleanPlacementList();
      dispatch(fetchSensorEquipmentPacket(weatherStationId));
      dispatch(fetchCameraViews(weatherStationId));
    }
    setLoading(true);
    const url = REST_API.WEATHER_STATIONS + weatherStationId;

    GetJson(url)
      .then((response) => response.json())
      .then((ws) => {
        if (isValueEmpty(ws)) {
          setWeatherStationData({});
        } else {
          setWeatherStationData(ws);
          if (ws.StatusTypeName && ['Active', 'Test'].includes(ws.StatusTypeName)) setIsCoordRequired(true);
        }
        setInitialDto({
          ...crudService.mapInitialValues({ entity: ws, getDefaultStatusType }),
        });
        setLoading(false);
      });
  }, []);

  const cleanPlacementList = () => {
    dispatch(setStatus(STATE_STATUS.IDLE));
    dispatch(setEditing(null));
    //segment
    dispatch(setSegment(null));
    dispatch(setOrignSegment(null));
    dispatch(setSotId(SENSOR_PLACEMENT_CONFIG.SOT_ID));
    dispatch(setSaveSegmentStatus(STATE_STATUS.IDLE));
    dispatch(setSaveSegmentError(null));
    //placement
    dispatch(setOrignPlacement(null));
    dispatch(setPlacement(null));
    dispatch(setSaveStatus(STATE_STATUS.IDLE));

    //camera
    dispatch(setCameraStatus(STATE_STATUS.IDLE));
  };
  const convertDropdownOptions = (options) => {
    if (isValueEmpty(options) || options.length === 0) return [];
    let newOptions = [];
    options.map((t) => {
      newOptions.push({ value: t.Id, label: t.DropdownText });
    });
    return newOptions;
  };
  const updateWeatherStation = (setSubmitting, resetForm, setErrors, weatherStationDto) => {
    const url = REST_API.WEATHER_STATIONS + weatherStationDto.Id;
    console.log(weatherStationDto);
    PutJson(url, weatherStationDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success(successMessage);
          const responseData = await response.json();
          setWeatherStationData(responseData);
          updateData(responseData);

          const updatedDto = {
            ...crudService.mapInitialValues({ entity: responseData, getDefaultStatusType }),
          };

          resetForm({
            values: updatedDto,
          });
          setInitialDto(updatedDto);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ WeatherStation: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createWeatherStation = (setSubmitting, resetForm, setErrors, weatherStationDto) => {
    const url = REST_API.WEATHER_STATIONS;
    PostJson(url, weatherStationDto)
      .then(async (response) => {
        if (response.ok) {
          setMode(FORM_MODE.VIEW);
          const responseData = await response.json();
          setWeatherStationData(responseData);
          updateData(responseData);
          toast.success(successMessage);
          dispatch(setWeatherStationId(responseData.Id));
          cleanPlacementList();
          dispatch(fetchSensorEquipmentPacket(responseData.Id));
          dispatch(fetchCameraViews(responseData.Id));

          const updatedDto = {
            ...crudService.mapInitialValues({ entity: responseData, getDefaultStatusType }),
          };
          resetForm({
            values: updatedDto,
          });
          setInitialDto(updatedDto);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ WeatherStation: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const deleteWeatherStation = () => {
    const id = weatherStationData.Id;
    const url = REST_API.WEATHER_STATIONS + id;

    DeleteJson(url, {}).then(async (response) => {
      if (response.ok) {
        setWeatherStationData({});
        removeData(id);
        toast.success(`${entityName} has been deleted`);
        setMode(FORM_MODE.LIST);
      } else if (response.status === 412) {
        const errors = await response.json();
        if (errors.entity) setEntityErrors({ WeatherStation: errors.entity });
      }
    });
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);

    const weatherStationDto = {
      Name: values.name.trim(),
      ShortName: isValueEmpty(values.shortName.trim()) ? null : values.shortName.trim(),
      Number: values.number.trim(),
      StatusTypeId: values.statustypeid ? values.statustypeid.value : null,
      TypeId: values.typeid && values.typeid.value > 0 ? values.typeid.value : null,
      Longitude: isValueEmpty(values.longitude) ? null : values.longitude,
      Latitude: isValueEmpty(values.latitude) ? null : values.latitude,
      Elevation: isValueEmpty(values.elevation) ? null : values.elevation,
      Description: isValueEmpty(values.description.trim()) ? null : values.description.trim(),
      OperationStartDate: isValueEmpty(values.operationstartdate)
        ? null
        : formatDate(values.operationstartdate, 'yyyy-MM-dd'),
      OperationEndDate: isValueEmpty(values.operationenddate)
        ? null
        : formatDate(values.operationenddate, 'yyyy-MM-dd'),
    };

    if (mode === FORM_MODE.EDIT) {
      updateWeatherStation(setSubmitting, resetForm, setErrors, {
        Id: weatherStationData.Id,
        ...weatherStationDto,
      });
    } else {
      createWeatherStation(setSubmitting, resetForm, setErrors, weatherStationDto);
    }
  };

  const updateData = (st) => {
    if (mode === FORM_MODE.EDIT) {
      const index = data.findIndex((x) => x.Id === st.Id);
      if (index < 0) return;
      data[index] = st;
      setData([...data]);
    } else {
      setData([...data, st]);
    }
  };
  const removeData = (Id) => {
    if (mode === FORM_MODE.EDIT) {
      let _data = [...data];
      let _removedData = [];
      _data.map((o) => {
        if (o.Id !== Id) {
          _removedData.push(o);
        }
      });
      setData([..._removedData]);
    }
  };
  const onClickDelete = () => {
    setShowDeleteConfirm(true);
  };
  const getDefaultStatusType = () => {
    if (!allWeatherStationStatuses || allWeatherStationStatuses.length === 0) return {};
    let s = allWeatherStationStatuses.find((ws) => ws.Name == 'Active');
    return { value: s.Id, label: s.Name };
  };
  const checkStatus = (e, setFieldValue) => {
    if (!isValueEmpty(e) && !isValueEmpty(e.label) && ['Active', 'Test'].includes(e.label)) {
      setIsCoordRequired(true);
    } else {
      setIsCoordRequired(false);
    }
  };

  return (
    <>
      {loading ? (
        <Container>
          <AwpSpinner />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={{
              ...crudService.mapInitialValues({ entity: weatherStationData, getDefaultStatusType }),
            }}
            initialTouched={{
              name: true,
              shortName: true,
              number: true,
              operationstartdate: true,
              operationenddate: true,
              latitude: true,
              longitude: true,
              elevation: true,
            }}
            validationSchema={Yup.object({
              name: Yup.string()
                .trim()
                .max(100, 'Maximum 100 characters are allowed')
                .required('Name is a required field'),
              shortName: Yup.string()
                .trim()
                .matches(/^[a-z0-9 ]+$/i, 'Special characters are not allowed')
                .max(10, 'Maximum 10 characters are allowed'),
              number: Yup.string()
                .trim()
                .max(5, 'Must be a numeric value with 5 characters')
                .required('Number is a required field'),
              description: Yup.string().trim().max(250, 'Maximum 250 characters are allowed'),
              elevation: Yup.string().test('checkwselevation', function () {
                if (!isValueEmpty(this.parent.elevation)) {
                  var regex = /^-?\d{1,5}$/;
                  if (!regex.test(this.parent.elevation)) {
                    return this.createError({
                      message: 'Elevation must be a whole number with a max of 5 characters.',
                      path: 'elevation',
                    });
                  }
                }
                if (
                  this.parent.statustypeid &&
                  (this.parent.statustypeid.label === 'Active' || this.parent.statustypeid.label === 'Test')
                ) {
                  if (isValueEmpty(this.parent.elevation)) {
                    return this.createError({
                      message: 'Elevation is required',
                      path: 'elevation',
                    });
                  }
                }
                if (!isValueEmpty(this.parent.elevation) && !isIntStr(this.parent.elevation)) {
                  return this.createError({
                    message: 'Please enter a valid number',
                    path: 'elevation',
                  });
                }
                return true;
              }),
              longitude: Yup.string()
                .trim()
                .test('checkwslongitude', function () {
                  if (
                    this.parent.statustypeid &&
                    (this.parent.statustypeid.label === 'Active' || this.parent.statustypeid.label === 'Test')
                  ) {
                    if (isValueEmpty(this.parent.longitude)) {
                      return this.createError({
                        message: 'Longitude is required',
                        path: 'longitude',
                      });
                    }
                  }
                  if (!isValueEmpty(this.parent.longitude) && !isIntStr(this.parent.longitude)) {
                    return this.createError({
                      message: 'Please enter a valid number',
                      path: 'longitude',
                    });
                  }
                  if (this.parent.longitude < -180.0 || this.parent.longitude > 180.0) {
                    return this.createError({
                      message: 'Please enter a valid number',
                      path: 'longitude',
                    });
                  }
                  return true;
                })
                .test('is-bc-longitude', 'Invalid British Columbia Longitude', function (value) {
                  return validateBcLongitude(value);
                }),
              latitude: Yup.string()
                .trim()
                .test('checkwslatitude', function () {
                  if (
                    this.parent.statustypeid &&
                    (this.parent.statustypeid.label === 'Active' || this.parent.statustypeid.label === 'Test')
                  ) {
                    if (isValueEmpty(this.parent.latitude)) {
                      return this.createError({
                        message: 'Latitude is required',
                        path: 'latitude',
                      });
                    }
                  }
                  if (!isValueEmpty(this.parent.latitude) && !isIntStr(this.parent.latitude)) {
                    return this.createError({
                      message: 'Please enter a valid number',
                      path: 'latitude',
                    });
                  }
                  if (this.parent.latitude < -90.0 || this.parent.latitude > 90.0) {
                    return this.createError({
                      message: 'Please enter a valid number',
                      path: 'latitude',
                    });
                  }
                  return true;
                })
                .test('is-bc-latitude', 'Invalid British Columbia Latitude', function (value) {
                  return validateBcLatitude(value);
                }),
              operationstartdate: Yup.date()
                .nullable()
                .test(
                  'startDate',
                  'Operation Start Date cannot be later than Operation End Date.',
                  (value, context) => {
                    if (
                      isValueEmpty(context.parent.operationstartdate) ||
                      isValueEmpty(context.parent.operationenddate)
                    )
                      return true;
                    console.log(value < context.parent.operationenddate);

                    return value < context.parent.operationenddate;
                  },
                ),
              operationenddate: Yup.date()
                .nullable()
                .test(
                  'endDate',
                  'Operation End Date cannot be earlier than Operation Start Date.',
                  (value, context) => {
                    if (
                      isValueEmpty(context.parent.operationstartdate) ||
                      isValueEmpty(context.parent.operationenddate)
                    )
                      return true;
                    return value > context.parent.operationstartdate;
                  },
                ),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form>
                <span className="awp-text-btn" onClick={() => setMode(FORM_MODE.LIST)}>
                  List Weather Stations
                </span>
                <span className="mx-3">&gt;</span>
                <span>{isValueEmpty(weatherStationData.Name) ? title : weatherStationData.Name}</span>
                <Card border="light" className="page-card">
                  <Card.Header>
                    <Row className="mt-4"></Row>
                    {crudService.crud.getHeader()}
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col sm={4} className="mb-4">
                        <FormInput
                          label="Number (required)"
                          instruction=""
                          readOnly={readOnly || weatherStationData.IsProtected === 'Y'}
                          name="number"
                          id="number"
                          maxLength={5}
                        />
                      </Col>
                      <Col sm={4} className="mb-4">
                        <FormInput
                          label="Name (required)"
                          instruction=""
                          readOnly={readOnly || weatherStationData.IsProtected === 'Y'}
                          name="name"
                          id="name"
                        />
                      </Col>
                      <Col sm={4} className="mb-4">
                        <FormInput
                          label="Short Name"
                          instruction=""
                          readOnly={readOnly || weatherStationData.IsProtected === 'Y'}
                          name="shortName"
                          id="shortName"
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={4} className="mb-4">
                        <FormSelect
                          readOnly={readOnly}
                          label="Status (required)"
                          id="statustypeid"
                          name="statustypeid"
                          textValue={weatherStationData.StatusTypeName}
                          options={convertDropdownOptions(allWeatherStationStatuses)}
                          isMulti={false}
                          postChange={(e) => checkStatus(e, props.setFieldValue)}
                        />
                      </Col>
                      <Col sm={4} className="mb-4">
                        <FormSelect
                          readOnly={readOnly}
                          label="Type"
                          id="typeid"
                          name="typeid"
                          textValue={weatherStationData.TypeName}
                          options={convertDropdownOptions(allWeatherStationTypes)}
                          isMulti={false}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={4} className="mb-4">
                        <ReactForm.Label className="flex-grow-1" style={{ marginBottom: '0rem' }}>
                          <b>{isCoordRequired ? 'Coordinates (required)' : 'Coordinates'}</b>
                        </ReactForm.Label>
                        <Row>
                          <Col sm={6} className="mb-4">
                            <FormSpatialInput
                              label={''}
                              instruction=""
                              readOnly={readOnly || weatherStationData.IsProtected === 'Y'}
                              name="latitude"
                              id="latitude"
                              subLabel={'Latitude'}
                            />
                          </Col>
                          <Col sm={6} className="mb-4">
                            <FormSpatialInput
                              label=""
                              instruction=""
                              readOnly={readOnly || weatherStationData.IsProtected === 'Y'}
                              name="longitude"
                              id="longitude"
                              subLabel={'Longitude'}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col sm={3} className="mb-4">
                        <FormSpatialInput
                          label={isCoordRequired ? 'Elevation (required)' : 'Elevation'}
                          instruction=""
                          readOnly={readOnly || weatherStationData.IsProtected === 'Y'}
                          name="elevation"
                          id="elevation"
                          subLabel="Metres"
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={3} className="mb-4">
                        <FormTextArea
                          readOnly={readOnly}
                          label="Location description"
                          instruction="(250 Characters max)"
                          id="description"
                          name="description"
                          rows={3}
                        />
                      </Col>
                      <Col sm={3} className="mb-4 pr-0 pl-0 pr-pl-mini-15">
                        <FormDatePicker
                          readOnly={readOnly}
                          label="Operation start date"
                          id="operationstartdate"
                          name="operationstartdate"
                          textValue={weatherStationData.OperationStartDate}
                          showTime={{ isEnabled: false }}
                          dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                        />
                      </Col>
                      <Col sm={3} className="mb-4 pr-0">
                        <FormDatePicker
                          readOnly={readOnly}
                          label="Operation end date"
                          id="operationenddate"
                          name="operationenddate"
                          textValue={weatherStationData.OperationEndDate}
                          showTime={{ isEnabled: false }}
                          dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                        />
                      </Col>
                    </Row>
                    <Row
                      className={
                        mode === FORM_MODE.CREATE ||
                        props.initialValues.latitude.toString() !== props.values.latitude.toString() ||
                        props.initialValues.longitude.toString() !== props.values.longitude.toString()
                          ? 'd-none'
                          : ''
                      }
                    >
                      <Col sm={4} className="mb-4">
                        <ReactForm.Label className="flex-grow-1" style={{ marginBottom: '0rem' }}>
                          <b>Geographic Areas</b>
                        </ReactForm.Label>
                        <Row>
                          <Col sm={6} className="mb-4">
                            <FormInput id="eet" name="eet" label={'EET'} readOnly={true} />
                          </Col>
                        </Row>
                        <Row>
                          <Col sm={6} className="mb-4">
                            <FormInput
                              id="maintenanceContractorArea"
                              name="maintenanceContractorArea"
                              label={'Maintenance Contractor Area'}
                              readOnly={true}
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Card.Body>
                  <Card.Footer>
                    {!showDeleteConfirm ? (
                      <Row className="my-2">
                        <AwpFormActionButtons
                          title=" "
                          id={2}
                          entityName="Weather Station"
                          buttonLabel={buttonLabel}
                          mode={mode}
                          setMode={setMode}
                          onClickDelete={onClickDelete}
                          permissions={[Permissions.WriteWeatherStation]}
                          readOnly={readOnly}
                          isAlignLeft={true}
                          btnCancelName={'Close'}
                          props={props}
                          isPristine={isEqual(
                            crudService.cleanFormValues({ entity: props.values }),
                            crudService.cleanFormValues({ entity: initialDto }),
                          )}
                        />
                      </Row>
                    ) : (
                      <Row className="my-2">
                        <AwpYesNoDialog
                          action={deleteWeatherStation}
                          setShowYesNoConfirm={setShowDeleteConfirm}
                          props={props}
                        />
                      </Row>
                    )}
                  </Card.Footer>
                </Card>
              </Form>
            )}
          </Formik>
          {mode === FORM_MODE.VIEW && (
            <div>
              <Row>
                <Col sm={12} className="mb-4">
                  <PlacementList />
                </Col>
              </Row>
              <Row>
                <Col sm={12} className="mb-4">
                  <WeatherStationCameraView />
                </Col>
              </Row>
            </div>
          )}
          <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
        </>
      )}
    </>
  );
};

export default WeatherStationEdit;
